import { cn } from '../../lib/utils.ts';

/**
 * Placeholder de chargement générique.
 *
 * @param props - Props HTML d'un `div`, dont `className` pour définir la forme.
 * @returns Bloc animé `animate-pulse` à utiliser quand la forme du contenu est connue.
 *
 * @example
 * ```tsx
 * <Skeleton className="h-4 w-32" />
 * ```
 */
function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="skeleton"
      className={cn('bg-accent animate-pulse rounded-md', className)}
      {...props}
    />
  );
}

/**
 * Placeholder rond pour avatar ou favicon.
 *
 * @param props - Props HTML d'un `div`.
 * @returns Skeleton circulaire par défaut en `size-10`.
 */
function SkeletonAvatar({ className, ...props }: React.ComponentProps<'div'>) {
  return <Skeleton className={cn('size-10 rounded-full', className)} {...props} />;
}

/**
 * Placeholder de ligne dense.
 *
 * @param props - Props HTML d'un `div`.
 * @returns Ligne composée d'un avatar, deux textes et une action factice.
 */
function SkeletonRow({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={cn('flex items-center gap-3', className)} {...props}>
      <SkeletonAvatar className="size-8" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3 w-2/5" />
        <Skeleton className="h-3 w-3/5" />
      </div>
      <Skeleton className="h-7 w-16" />
    </div>
  );
}

/**
 * Placeholder de carte complète.
 *
 * @param props - Props HTML d'un `div`.
 * @returns Carte squelette avec header, corps et actions.
 */
function SkeletonCard({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={cn('border-border space-y-4 rounded-lg border p-4', className)} {...props}>
      <div className="flex items-center gap-3">
        <SkeletonAvatar />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-20 w-full" />
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-20" />
      </div>
    </div>
  );
}

export { Skeleton, SkeletonAvatar, SkeletonCard, SkeletonRow };
